import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Car, 
  Eye,
  Search,
  LayoutDashboard,
  LogOut,
  Star,
  StarOff,
  FolderOpen,
  RotateCcw,
  Archive,
  CheckCircle,
  Upload,
  Download,
  FileSpreadsheet,
  Settings,
  Tag
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { api, formatPrice } from '@/lib/api';
import { toast } from 'sonner';

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [permanentDelete, setPermanentDelete] = useState(false);
  const [emptyingTrash, setEmptyingTrash] = useState(false);
  const [activeTab, setActiveTab] = useState('publicado');
  const [importing, setImporting] = useState(false);
  const excelInputRef = useRef(null);
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    total_pages: 1
  });

  const fetchVehicles = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, page_size: 20, status: activeTab };
      if (search) params.search = search;
      
      const data = await api.getVehicles(params);
      setVehicles(data.vehicles);
      setPagination({
        page: data.page,
        total: data.total,
        total_pages: data.total_pages
      });
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast.error('Error al cargar los vehículos');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await api.getStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchVehicles();
    fetchStats();
  }, [activeTab]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchVehicles(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.deleteVehicle(deleteId, permanentDelete);
      toast.success(permanentDelete ? 'Vehículo eliminado permanentemente' : 'Vehículo movido a papelera');
      fetchVehicles(pagination.page);
      fetchStats();
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      toast.error('Error al eliminar el vehículo');
    } finally {
      setDeleteId(null);
      setPermanentDelete(false);
    }
  };

  const handleStatusChange = async (vehicleId, newStatus) => {
    try {
      await api.updateVehicle(vehicleId, { status: newStatus });
      const messages = {
        publicado: 'Vehículo publicado',
        inactivo: 'Vehículo desactivado',
        papelera: 'Vehículo movido a papelera'
      };
      toast.success(messages[newStatus]);
      fetchVehicles(pagination.page);
      fetchStats();
    } catch (error) {
      toast.error('Error al cambiar el estado');
    }
  };

  const handleEmptyTrash = async () => {
    try {
      const result = await api.emptyTrash();
      toast.success(result.message);
      fetchVehicles(1);
      fetchStats();
    } catch (error) {
      toast.error('Error al vaciar la papelera');
    } finally {
      setEmptyingTrash(false);
    }
  };

  const handleToggleDestacado = async (vehicle) => {
    try {
      await api.updateVehicle(vehicle.id, { destacado: !vehicle.destacado });
      toast.success(vehicle.destacado ? 'Quitado de destacados' : 'Añadido a destacados');
      fetchVehicles(pagination.page);
      fetchStats();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleExportExcel = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Error al exportar');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vehiculos_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel exportado correctamente');
    } catch (error) {
      toast.error('Error al exportar Excel');
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/import/excel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Error');
      toast.success(`${data.updated} actualizados, ${data.created} creados, ${data.errors} errores`);
      fetchVehicles(1);
      fetchStats();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setImporting(false);
      if (excelInputRef.current) excelInputRef.current.value = '';
    }
  };

  const statusTabs = [
    { key: 'publicado', label: 'Publicados', count: stats?.publicados || 0, icon: CheckCircle },
    { key: 'inactivo', label: 'Inactivos', count: stats?.inactivos || 0, icon: Archive },
    { key: 'papelera', label: 'Papelera', count: stats?.papelera || 0, icon: Trash2 },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-card border-r border-border hidden lg:block">
        <div className="p-6">
          <Link to="/">
            <img src="/logo.png" alt="Chocados GDL" className="h-10" />
          </Link>
        </div>
        
        <nav className="px-4 space-y-2">
          <Link
            to="/admin"
            className="flex items-center gap-3 px-4 py-3 rounded-sm bg-primary/10 text-primary font-medium"
          >
            <LayoutDashboard className="w-5 h-5" />
            Dashboard
          </Link>
          <Link
            to="/admin/vehiculos/nuevo"
            className="flex items-center gap-3 px-4 py-3 rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <Plus className="w-5 h-5" />
            Nuevo Vehículo
          </Link>
          <Link
            to="/admin/categorias"
            className="flex items-center gap-3 px-4 py-3 rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <FolderOpen className="w-5 h-5" />
            Categorías
          </Link>
          <Link
            to="/admin/marcas"
            className="flex items-center gap-3 px-4 py-3 rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <Tag className="w-5 h-5" />
            Marcas
          </Link>
          <Link
            to="/admin/importar"
            className="flex items-center gap-3 px-4 py-3 rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <Upload className="w-5 h-5" />
            Importar WordPress
          </Link>
          <Link
            to="/admin/configuracion"
            className="flex items-center gap-3 px-4 py-3 rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <Settings className="w-5 h-5" />
            Configuracion
          </Link>
          <Link
            to="/"
            className="flex items-center gap-3 px-4 py-3 rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <Eye className="w-5 h-5" />
            Ver Sitio
          </Link>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border">
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-muted-foreground hover:text-foreground"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 md:p-8">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between mb-6">
          <Link to="/">
            <img src="/logo.png" alt="Chocados GDL" className="h-8" />
          </Link>
          <Button variant="ghost" size="icon" onClick={handleLogout}>
            <LogOut className="w-5 h-5" />
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-card border border-border rounded-sm p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Car className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Vehículos</p>
                <p className="text-2xl font-bold">{stats?.total_vehicles || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-sm p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center">
                <Star className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Destacados</p>
                <p className="text-2xl font-bold">{stats?.destacados || 0}</p>
              </div>
            </div>
          </div>
          {stats?.by_type && Object.entries(stats.by_type).slice(0, 2).map(([type, count]) => (
            <div key={type} className="bg-card border border-border rounded-sm p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                  <Car className="w-6 h-6 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{type}</p>
                  <p className="text-2xl font-bold">{count}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Status Tabs */}
        <div className="flex items-center gap-1 mb-6 border-b border-border pb-0">
          {statusTabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setSearch(''); }}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key 
                  ? 'border-primary text-primary' 
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              <span className={`text-xs px-1.5 py-0.5 rounded ${
                activeTab === tab.key ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <h1 className="text-2xl font-bold uppercase tracking-wider">
            {activeTab === 'publicado' ? 'Inventario de Vehículos' : activeTab === 'inactivo' ? 'Vehículos Inactivos' : 'Papelera'}
          </h1>
          <div className="flex flex-wrap gap-2">
            {activeTab === 'papelera' && (stats?.papelera || 0) > 0 && (
              <Button 
                variant="destructive" 
                onClick={() => setEmptyingTrash(true)}
                data-testid="empty-trash-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Vaciar Papelera
              </Button>
            )}
            <Button variant="outline" onClick={handleExportExcel} data-testid="export-excel-btn">
              <Download className="w-4 h-4 mr-2" />
              Exportar Excel
            </Button>
            <Button variant="outline" onClick={() => excelInputRef.current?.click()} disabled={importing} data-testid="import-excel-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              {importing ? 'Importando...' : 'Importar Excel'}
            </Button>
            <input ref={excelInputRef} type="file" accept=".xlsx,.xls" onChange={handleImportExcel} className="hidden" />
            <Link to="/admin/vehiculos/nuevo">
              <Button className="btn-primary" data-testid="new-vehicle-btn">
                <Plus className="w-5 h-5 mr-2" />
                Nuevo Vehículo
              </Button>
            </Link>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Buscar vehículos..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-input border-transparent focus:border-primary max-w-md"
            data-testid="admin-search"
          />
        </div>

        {/* Table */}
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">Imagen</TableHead>
                <TableHead className="text-muted-foreground">Título</TableHead>
                <TableHead className="text-muted-foreground">Tipo</TableHead>
                <TableHead className="text-muted-foreground">Marca</TableHead>
                <TableHead className="text-muted-foreground">Precio</TableHead>
                <TableHead className="text-muted-foreground">Estado</TableHead>
                <TableHead className="text-muted-foreground text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i} className="border-border">
                    <TableCell colSpan={7}>
                      <div className="h-12 bg-muted animate-pulse rounded" />
                    </TableCell>
                  </TableRow>
                ))
              ) : vehicles.length > 0 ? (
                vehicles.map((vehicle) => {
                  const imageUrl = vehicle.imagenes?.[0]?.startsWith('/api')
                    ? `${process.env.REACT_APP_BACKEND_URL}${vehicle.imagenes[0]}`
                    : vehicle.imagenes?.[0] || 'https://via.placeholder.com/80x60?text=Sin+imagen';
                  
                  return (
                    <TableRow 
                      key={vehicle.id} 
                      className="border-border"
                      data-testid={`vehicle-row-${vehicle.id}`}
                    >
                      <TableCell>
                        <img
                          src={imageUrl}
                          alt={vehicle.titulo}
                          className="w-20 h-14 object-cover rounded"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/80x60?text=Sin+imagen';
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <p className="font-medium line-clamp-1 max-w-[200px]">
                          {vehicle.titulo}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {vehicle.modelo} • {vehicle.color}
                        </p>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{vehicle.tipo}</Badge>
                      </TableCell>
                      <TableCell>{vehicle.marca}</TableCell>
                      <TableCell className="font-bold text-primary">
                        {formatPrice(vehicle.precio)}
                      </TableCell>
                      <TableCell>
                        {vehicle.destacado && (
                          <Badge className="bg-primary/20 text-primary border-primary/30">
                            <Star className="w-3 h-3 mr-1 fill-current" />
                            Destacado
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          {activeTab === 'papelera' ? (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleStatusChange(vehicle.id, 'publicado')}
                                title="Recuperar"
                                className="text-green-500 hover:text-green-400"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => { setDeleteId(vehicle.id); setPermanentDelete(true); }}
                                className="text-destructive hover:text-destructive"
                                title="Eliminar definitivamente"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          ) : activeTab === 'inactivo' ? (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleStatusChange(vehicle.id, 'publicado')}
                                title="Publicar"
                                className="text-green-500 hover:text-green-400"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Link to={`/admin/vehiculos/${vehicle.id}`}>
                                <Button variant="ghost" size="icon" title="Editar">
                                  <Pencil className="w-4 h-4" />
                                </Button>
                              </Link>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setDeleteId(vehicle.id)}
                                className="text-destructive hover:text-destructive"
                                title="Mover a papelera"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleToggleDestacado(vehicle)}
                                title={vehicle.destacado ? 'Quitar de destacados' : 'Añadir a destacados'}
                              >
                                {vehicle.destacado ? (
                                  <StarOff className="w-4 h-4 text-muted-foreground" />
                                ) : (
                                  <Star className="w-4 h-4 text-muted-foreground" />
                                )}
                              </Button>
                              <Link to={`/vehiculo/${vehicle.slug || vehicle.id}`}>
                                <Button variant="ghost" size="icon" title="Ver">
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </Link>
                              <Link to={`/admin/vehiculos/${vehicle.id}`}>
                                <Button variant="ghost" size="icon" title="Editar">
                                  <Pencil className="w-4 h-4" />
                                </Button>
                              </Link>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleStatusChange(vehicle.id, 'inactivo')}
                                title="Desactivar"
                                className="text-yellow-500 hover:text-yellow-400"
                              >
                                <Archive className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setDeleteId(vehicle.id)}
                                className="text-destructive hover:text-destructive"
                                title="Mover a papelera"
                                data-testid={`delete-btn-${vehicle.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                    No se encontraron vehículos
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <p className="text-sm text-muted-foreground">
              Mostrando {vehicles.length} de {pagination.total} vehículos
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => fetchVehicles(pagination.page - 1)}
                disabled={pagination.page <= 1}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                onClick={() => fetchVehicles(pagination.page + 1)}
                disabled={pagination.page >= pagination.total_pages}
              >
                Siguiente
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => { setDeleteId(null); setPermanentDelete(false); }}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {permanentDelete ? '¿Eliminar definitivamente?' : '¿Mover a papelera?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {permanentDelete 
                ? 'Esta acción no se puede deshacer. El vehículo y todas sus imágenes serán eliminados permanentemente del servidor.'
                : 'El vehículo será movido a la papelera. Podrás recuperarlo más tarde.'
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
            >
              {permanentDelete ? 'Eliminar Definitivamente' : 'Mover a Papelera'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Empty Trash Confirmation Dialog */}
      <AlertDialog open={emptyingTrash} onOpenChange={() => setEmptyingTrash(false)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Vaciar papelera?</AlertDialogTitle>
            <AlertDialogDescription>
              Todos los vehículos en la papelera serán eliminados permanentemente junto con sus imágenes. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleEmptyTrash}
              className="bg-destructive hover:bg-destructive/90"
            >
              Vaciar Papelera
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
